import { useState, useEffect } from 'react';
import {
  X,
  ArrowDownLeft,
  ArrowUpRight,
  Loader2,
  CheckCircle2,
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import api from '../config/api';
import { pakistanToday } from '../utils/pakistanTime';

const QUICK_AMOUNTS = [1000, 2000, 5000, 10000];
const PAYMENT_SOURCES = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'online', label: 'Online Wallet' },
  { value: 'bank', label: 'Bank' },
];

function formatCurrency(val) {
  if (val === null || val === undefined || isNaN(Number(val))) return 'Rs. 0';
  const num = Number(val);
  return `Rs. ${num.toLocaleString('en-PK', {
    minimumFractionDigits: num % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

export default function CashTransactionModal({
  isOpen,
  onClose,
  onSuccess,
  initialType = 'deposit',
  currentRemainingCash = 0,
  accountBalances = {},
}) {
  const [type, setType] = useState(initialType);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [transactionDate, setTransactionDate] = useState(() => pakistanToday());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setType(initialType);
      setPaymentMethod('cash');
      setAmount('');
      setReason(initialType === 'deposit' ? 'Personal Account Deposit' : 'Personal Account Withdrawal');
      setNotes('');
      setTransactionDate(pakistanToday());
    }
  }, [isOpen, initialType]);

  const handleTypeSwitch = (newType) => {
    setType(newType);
    setReason(newType === 'deposit' ? 'Personal Account Deposit' : 'Personal Account Withdrawal');
  };

  const numericAmount = parseFloat(amount) || 0;
  const selectedSource = PAYMENT_SOURCES.find((source) => source.value === paymentMethod) || PAYMENT_SOURCES[0];
  const currentBalance = Number(accountBalances[paymentMethod] ?? (paymentMethod === 'cash' ? currentRemainingCash : 0));
  const projectedBalance =
    type === 'deposit'
      ? currentBalance + numericAmount
      : currentBalance - numericAmount;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!numericAmount || numericAmount <= 0) {
      toast.error('Please enter a valid amount greater than zero');
      return;
    }

    const finalCategory = reason.trim() || (type === 'deposit' ? 'Personal Account Deposit' : 'Personal Account Withdrawal');

    setSubmitting(true);
    try {
      const payload = {
        type,
        amount: numericAmount,
        payment_method: paymentMethod,
        account_name: `Personal Account (${selectedSource.label})`,
        category: finalCategory,
        notes: notes.trim(),
        transaction_date: transactionDate,
      };

      const res = await api.post('/cash-transactions', payload);
      toast.success(
        res.message ||
          `Cash ${type === 'deposit' ? 'Deposit' : 'Withdrawal'} of ${formatCurrency(numericAmount)} recorded successfully!`
      );
      if (onSuccess) {
        onSuccess(res);
      }
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to save transaction');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 transition-opacity animate-in fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl transition-all">
        {/* Compact Header */}
        <div className="border-b border-slate-100 bg-slate-50/70 px-4 py-3 sm:px-5 sm:py-3.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div
                className={clsx(
                  'flex h-8 w-8 items-center justify-center rounded-lg border',
                  type === 'deposit'
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-600'
                    : 'border-rose-200 bg-rose-50 text-rose-600'
                )}
              >
                {type === 'deposit' ? (
                  <ArrowDownLeft className="h-4 w-4 stroke-[2.5]" />
                ) : (
                  <ArrowUpRight className="h-4 w-4 stroke-[2.5]" />
                )}
              </div>
              <div>
                <h2 className="text-sm font-bold text-slate-900">
                  {type === 'deposit' ? 'Deposit Funds' : 'Withdraw Funds'}
                </h2>
                <p className="text-[11px] text-slate-500">
                  Owner Personal Account
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-200/60 hover:text-slate-600 transition"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Compact Switch Tabs */}
          <div className="mt-2.5 grid grid-cols-2 gap-1 rounded-lg bg-slate-200/70 p-0.5 text-xs font-semibold">
            <button
              type="button"
              onClick={() => handleTypeSwitch('deposit')}
              className={clsx(
                'flex items-center justify-center gap-1.5 rounded-md py-1.5 text-xs font-bold transition-all',
                type === 'deposit'
                  ? 'bg-white text-emerald-700 shadow-xs ring-1 ring-emerald-500/20'
                  : 'text-slate-600 hover:text-slate-900'
              )}
            >
              <ArrowDownLeft className="h-3.5 w-3.5 text-emerald-600" />
              <span>Deposit (+)</span>
            </button>

            <button
              type="button"
              onClick={() => handleTypeSwitch('withdraw')}
              className={clsx(
                'flex items-center justify-center gap-1.5 rounded-md py-1.5 text-xs font-bold transition-all',
                type === 'withdraw'
                  ? 'bg-white text-rose-700 shadow-xs ring-1 ring-rose-500/20'
                  : 'text-slate-600 hover:text-slate-900'
              )}
            >
              <ArrowUpRight className="h-3.5 w-3.5 text-rose-600" />
              <span>Withdraw (-)</span>
            </button>
          </div>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-5 space-y-3">
          <div>
            <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-slate-700">
              Balance Source
            </label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-800 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            >
              {PAYMENT_SOURCES.map((source) => (
                <option key={source.value} value={source.value}>{source.label}</option>
              ))}
            </select>
          </div>

          {/* Current & Projected Balance Indicator Strip */}
          <div
            className={clsx(
              'flex items-center justify-between rounded-lg border px-3 py-2 text-xs',
              type === 'deposit'
                ? 'border-emerald-200 bg-emerald-50/50'
                : 'border-rose-200 bg-rose-50/50'
            )}
          >
            <div>
              <span className="text-[10px] uppercase font-bold text-slate-500 block leading-tight">
                Current {selectedSource.label} Balance
              </span>
              <span className="font-bold text-slate-800 text-xs">
                {formatCurrency(currentBalance)}
              </span>
            </div>

            {numericAmount > 0 && (
              <div className="text-right">
                <span
                  className={clsx(
                    'text-[10px] uppercase font-bold block leading-tight',
                    type === 'deposit' ? 'text-emerald-700' : 'text-rose-700'
                  )}
                >
                  Projected Balance
                </span>
                <span
                  className={clsx(
                    'font-black text-xs',
                    type === 'deposit' ? 'text-emerald-700' : 'text-rose-700'
                  )}
                >
                  {formatCurrency(projectedBalance)}
                </span>
              </div>
            )}
          </div>

          {/* Amount Input with Quick Pills */}
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1">
              Amount (Rs.) <span className="text-rose-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">
                Rs.
              </span>
              <input
                type="number"
                step="any"
                min="1"
                required
                autoFocus
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-base font-bold text-slate-900 placeholder:text-slate-300 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 transition"
              />
            </div>

            {/* Quick Pills */}
            <div className="mt-1.5 flex items-center gap-1.5">
              {QUICK_AMOUNTS.map((amt) => (
                <button
                  key={amt}
                  type="button"
                  onClick={() => setAmount(String(amt))}
                  className="flex-1 rounded border border-slate-200 bg-slate-50 py-0.5 text-[11px] font-semibold text-slate-600 hover:border-emerald-400 hover:bg-emerald-50 hover:text-emerald-700 transition"
                >
                  +{amt >= 1000 ? `${amt / 1000}k` : amt}
                </button>
              ))}
            </div>
          </div>

          {/* 2-Column: Reason & Date */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1">
                Reason / Title
              </label>
              <input
                type="text"
                placeholder={type === 'deposit' ? 'Personal Deposit' : 'Personal Withdrawal'}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-800 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>

            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1">
                Date <span className="text-rose-500">*</span>
              </label>
              <input
                type="date"
                required
                value={transactionDate}
                onChange={(e) => setTransactionDate(e.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-800 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>
          </div>

          {/* Note (Optional) */}
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1">
              Note (Optional)
            </label>
            <input
              type="text"
              placeholder="Any additional remarks..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-800 placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>

          {/* Action Buttons */}
          <div className="pt-2 flex items-center justify-end gap-2 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-lg border border-slate-200 px-3.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={submitting || !numericAmount || numericAmount <= 0}
              className={clsx(
                'inline-flex items-center justify-center gap-1.5 rounded-lg px-4 py-1.5 text-xs font-bold text-white shadow-xs transition disabled:opacity-50 disabled:cursor-not-allowed',
                type === 'deposit'
                  ? 'bg-emerald-600 hover:bg-emerald-700'
                  : 'bg-rose-600 hover:bg-rose-700'
              )}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  <span>
                    Confirm {type === 'deposit' ? 'Deposit' : 'Withdrawal'}
                  </span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
